/**
 * Whether Fias Forge should be offered to the signed-in user, and whether
 * their plan lets them use it (ADR-023 D8).
 *
 * Reads the same `GET /v1/forge/usage` query the usage panel does (one cache
 * entry per user), which reports the platform kill switch (`enabled`) and
 * plan eligibility (`eligible`). Any failure reads as "not offered": a
 * surface that cannot confirm Forge is on must not advertise it.
 */

import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { forgeQueryKeys } from '../api/queryKeys';

export interface ForgeAvailability {
  /** The kill switch is on — show Forge as an option at all. */
  offered: boolean;
  /** The user's plan can run forges (Standard/Pro). */
  eligible: boolean;
  isLoading: boolean;
}

/**
 * `enabled: false` skips the request and reads as "not offered". Callers pass
 * it for a guest-trial account: `GET /v1/forge/usage` is full-account-only, and
 * its 403 GUEST_ACCOUNT_RESTRICTED would send the guest to sign-up from a page
 * they are allowed to be on (the builder's sessions list).
 */
export function useForgeAvailability(options: { enabled?: boolean } = {}): ForgeAvailability {
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: forgeQueryKeys.usage(),
    queryFn: () => api.getForgeUsage(),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
  return {
    offered: enabled && query.data?.enabled === true,
    eligible: enabled && query.data?.eligible === true,
    isLoading: enabled && query.isLoading,
  };
}
